import { useEffect, useSyncExternalStore } from "react";

const STORAGE_KEY = "conspiracy-board:v1";

const DEFAULT_CATEGORIES = [
  { id: "cat-people", name: "People", color: "#3B82F6" },
  { id: "cat-places", name: "Places", color: "#EF4444" },
  { id: "cat-orgs", name: "Organizations", color: "#10B981" },
  { id: "cat-events", name: "Events", color: "#F59E0B" },
  { id: "cat-apps", name: "Apps", color: "#A855F7" },
  { id: "cat-ip", name: "IP Addresses", color: "#4bfa00" }
];

const genId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const initialState = () => {
  if (typeof window === "undefined") return blank();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    console.log(raw)
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...blank(), ...parsed };
    }
  } catch (e) {
    console.warn("Failed to load board state", e);
  }
  return blank();
};

function blank() {
  return {
    categories: DEFAULT_CATEGORIES.slice(),
    cards: [],
    connections: [],
    locked: false,
  };
}

let state = initialState();
const listeners = new Set();

const persist = () => {
  try {
    const { categories, cards, connections, locked } = state;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ categories, cards, connections, locked })
    );
  } catch (e) {
    console.warn("Failed to persist board state", e);
  }
};

const emit = () => {
  persist();
  listeners.forEach((l) => l());
};

const setState = (updater) => {
  state = typeof updater === "function" ? updater(state) : { ...state, ...updater };
  emit();
};

export const boardStore = {
  getState: () => state,
  subscribe: (cb) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
  // Cards
  addCard: (partial) => {
    const id = partial.id || `card-${genId()}`;
    const card = {
      id,
      text: partial.text || "",
      photo: partial.photo || null,
      caption: partial.caption || "",
      categoryId: partial.categoryId || state.categories[0]?.id || null,
      x: partial.x ?? 200 + Math.random() * 400,
      y: partial.y ?? 160 + Math.random() * 300,
      rotation: partial.rotation ?? (Math.random() * 6 - 3),
    };
    setState((s) => ({ ...s, cards: [...s.cards, card] }));
    return card;
  },
  updateCard: (id, patch) => {
    setState((s) => ({
      ...s,
      cards: s.cards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
  },
  moveCard: (id, x, y) => {
    setState((s) => ({
      ...s,
      cards: s.cards.map((c) => (c.id === id ? { ...c, x, y } : c)),
    }));
  },
  removeCard: (id) => {
    setState((s) => ({
      ...s,
      cards: s.cards.filter((c) => c.id !== id),
      connections: s.connections.filter((cn) => cn.a !== id && cn.b !== id),
    }));
  },
  // Categories
  addCategory: (name, color) => {
    const cat = { id: `cat-${genId()}`, name, color };
    setState((s) => ({ ...s, categories: [...s.categories, cat] }));
    return cat;
  },
  updateCategory: (id, patch) => {
    setState((s) => ({
      ...s,
      categories: s.categories.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
  },
  removeCategory: (id) => {
    setState((s) => {
      const fallback = s.categories.find((c) => c.id !== id)?.id || null;
      return {
        ...s,
        categories: s.categories.filter((c) => c.id !== id),
        cards: s.cards.map((c) =>
          c.categoryId === id ? { ...c, categoryId: fallback } : c
        ),
      };
    });
  },
  // Connections (bidirectional; stored once)
  addConnection: (a, b, label = "", color = "#DC2626") => {
    if (a === b) return null;
    const exists = state.connections.some(
      (c) => (c.a === a && c.b === b) || (c.a === b && c.b === a)
    );
    if (exists) return null;
    const conn = { id: `conn-${genId()}`, a, b, label, color, pins: [] };
    setState((s) => ({ ...s, connections: [...s.connections, conn] }));
    return conn;
  },
  updateConnection: (id, patch) => {
    setState((s) => ({
      ...s,
      connections: s.connections.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      ),
    }));
  },
  addPin: (connId, index, x, y) => {
    setState((s) => ({
      ...s,
      connections: s.connections.map((c) => {
        if (c.id !== connId) return c;
        const pins = c.pins ? [...c.pins] : [];
        const at = Math.max(0, Math.min(index, pins.length));
        pins.splice(at, 0, { x, y });
        return { ...c, pins };
      }),
    }));
  },
  movePin: (connId, index, x, y) => {
    setState((s) => ({
      ...s,
      connections: s.connections.map((c) => {
        if (c.id !== connId) return c;
        const pins = (c.pins || []).map((p, i) =>
          i === index ? { x, y } : p
        );
        return { ...c, pins };
      }),
    }));
  },
  removePin: (connId, index) => {
    setState((s) => ({
      ...s,
      connections: s.connections.map((c) => {
        if (c.id !== connId) return c;
        const pins = (c.pins || []).filter((_, i) => i !== index);
        return { ...c, pins };
      }),
    }));
  },
  removeConnection: (id) => {
    setState((s) => ({
      ...s,
      connections: s.connections.filter((c) => c.id !== id),
    }));
  },
  removeConnectionBetween: (a, b) => {
    setState((s) => ({
      ...s,
      connections: s.connections.filter(
        (c) => !((c.a === a && c.b === b) || (c.a === b && c.b === a))
      ),
    }));
  },
  toggleLock: () => setState((s) => ({ ...s, locked: !s.locked })),
  clearAll: () => setState(() => blank()),
};

export const useBoard = () =>
  useSyncExternalStore(boardStore.subscribe, boardStore.getState, boardStore.getState);

export const useBoardSelector = (selector) => {
  const s = useBoard();
  return selector(s);
};

export const getConnectionsForCard = (cardId, connections) =>
  connections.filter((c) => c.a === cardId || c.b === cardId);

export const otherEnd = (conn, cardId) => (conn.a === cardId ? conn.b : conn.a);

export const useEnsureDefaults = () => {
  useEffect(() => {
    if (state.categories.length === 0) {
      setState((s) => ({ ...s, categories: DEFAULT_CATEGORIES.slice() }));
    }
  }, []);
};

export { STORAGE_KEY };
